import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { 
  HomeIcon, 
  UserGroupIcon, 
  ChatBubbleLeftRightIcon,
  UserIcon,
  ChevronDownIcon,
  GlobeAltIcon,
  MapIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  UserIcon as UserIconSolid,
  MapIcon as MapIconSolid,
} from '@heroicons/react/24/solid';
// Bicycle icon - custom SVG
const BicycleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);
const BicycleIconSolid = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { logout, user } = useAuth();
  const location = useLocation();
  const [friendsDropdownOpen, setFriendsDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const friendsDropdownRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  const navigation = [
    { name: t('navigation.feed'), href: '/feed', icon: HomeIcon, iconSolid: HomeIconSolid },
    { name: t('navigation.rides'), href: '/rides', icon: BicycleIcon, iconSolid: BicycleIconSolid },
    { name: t('navigation.discover') || 'Discover', href: '/discover', icon: MapIcon, iconSolid: MapIconSolid },
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (friendsDropdownRef.current && !friendsDropdownRef.current.contains(event.target as Node)) {
        setFriendsDropdownOpen(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => {
    if (path === '/feed') {
      return location.pathname === '/feed' || location.pathname === '/';
    }
    if (path === '/discover') {
      return location.pathname.startsWith('/discover') || location.pathname.startsWith('/stores') || location.pathname.startsWith('/clubs');
    }
    return location.pathname.startsWith(path);
  };

  const isFriendsActive = () => {
    return location.pathname.startsWith('/friends') || location.pathname.startsWith('/messages');
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setProfileDropdownOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation - Desktop */}
      <nav className="hidden md:flex bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/feed" className="flex items-center">
                  <img
                    src="/logo.png"
                    alt="RideConnect"
                    className="h-8 w-auto"
                    onError={(e) => {
                      // Fallback to text if image fails
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = document.createElement('h1');
                      fallback.className = 'text-xl font-bold text-primary-600';
                      fallback.textContent = 'RideConnect';
                      target.parentNode?.insertBefore(fallback, target);
                    }}
                  />
                </Link>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navigation.map((item) => {
                  const Icon = isActive(item.href) ? item.iconSolid : item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        isActive(item.href)
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      <Icon className="h-5 w-5 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
                
                {/* Friends Dropdown with Messages */}
                <div className="relative" ref={friendsDropdownRef}>
                  <button
                    onClick={() => setFriendsDropdownOpen(!friendsDropdownOpen)}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isFriendsActive()
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                    style={{ height: '100%' }}
                  >
                    {isFriendsActive() ? (
                      <UserGroupIconSolid className="h-5 w-5 mr-2" />
                    ) : (
                      <UserGroupIcon className="h-5 w-5 mr-2" />
                    )}
                    <span>{t('navigation.friends')}</span>
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                  </button>
                  {friendsDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50">
                      <div className="py-1">
                        <Link
                          to="/friends"
                          onClick={() => setFriendsDropdownOpen(false)}
                          className={`block px-4 py-2 text-sm ${
                            location.pathname.startsWith('/friends') && !location.pathname.startsWith('/messages')
                              ? 'bg-primary-50 text-primary-600'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {t('navigation.friends')}
                        </Link>
                        <Link
                          to="/messages"
                          onClick={() => setFriendsDropdownOpen(false)}
                          className={`block px-4 py-2 text-sm ${
                            location.pathname.startsWith('/messages')
                              ? 'bg-primary-50 text-primary-600'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {t('navigation.messages')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Profile Dropdown */}
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center text-gray-500 hover:text-gray-700"
                >
                  {location.pathname.startsWith('/profile') ? (
                    <UserIconSolid className="h-6 w-6" />
                  ) : (
                    <UserIcon className="h-6 w-6" />
                  )}
                </button>
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50">
                    <div className="py-1">
                      <Link
                        to="/profile"
                        onClick={() => setProfileDropdownOpen(false)}
                        className={`block px-4 py-2 text-sm ${
                          location.pathname.startsWith('/profile')
                            ? 'bg-primary-50 text-primary-600'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {t('navigation.profile')}
                      </Link>
                      <div className="border-t border-gray-100">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                          {t('common.language')}
                        </div>
                        <button
                          onClick={() => changeLanguage('en')}
                          className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                            i18n.language === 'en' ? 'bg-primary-50 text-primary-600' : 'text-gray-700'
                          }`}
                        >
                          <div className="flex items-center">
                            <GlobeAltIcon className="h-4 w-4 mr-2" />
                            English
                          </div>
                        </button>
                        <button
                          onClick={() => changeLanguage('es')}
                          className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                            i18n.language === 'es' ? 'bg-primary-50 text-primary-600' : 'text-gray-700'
                          }`}
                        >
                          <div className="flex items-center">
                            <GlobeAltIcon className="h-4 w-4 mr-2" />
                            Español
                          </div>
                        </button>
                      </div>
                      <div className="border-t border-gray-100">
                        <button
                          onClick={() => {
                            logout();
                            setProfileDropdownOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          {t('auth.logout')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Bottom Navigation - Mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex justify-around">
          {navigation.map((item) => {
            const Icon = isActive(item.href) ? item.iconSolid : item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center justify-center flex-1 py-2 ${
                  isActive(item.href)
                    ? 'text-primary-600'
                    : 'text-gray-500'
                }`}
              >
                <Icon className="h-6 w-6" />
                <span className="text-xs mt-1">{item.name}</span>
              </Link>
            );
          })}
          {/* Friends on Mobile */}
          <Link
            to="/friends"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${
              isFriendsActive()
                ? 'text-primary-600'
                : 'text-gray-500'
            }`}
          >
            {isFriendsActive() ? (
              <UserGroupIconSolid className="h-6 w-6" />
            ) : (
              <UserGroupIcon className="h-6 w-6" />
            )}
            <span className="text-xs mt-1">{t('navigation.friends')}</span>
          </Link>
          {/* Profile on Mobile */}
          <Link
            to="/profile"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${
              location.pathname.startsWith('/profile')
                ? 'text-primary-600'
                : 'text-gray-500'
            }`}
          >
            {location.pathname.startsWith('/profile') ? (
              <UserIconSolid className="h-6 w-6" />
            ) : (
              <UserIcon className="h-6 w-6" />
            )}
            <span className="text-xs mt-1">{t('navigation.profile')}</span>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className={`${location.pathname.startsWith('/messages') ? '' : 'pb-16 md:pb-0'}`}>
        {children}
      </main>
    </div>
  );
};

export default MainLayout;
